import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Mail, Phone, Sparkles, Copy, Loader2,
  RefreshCw, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AITools = () => {
  const [searchParams] = useSearchParams();
  const leadIdParam = searchParams.get('lead');
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState(tabParam === 'script' ? 'script' : 'email');
  const [savedLeads, setSavedLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(leadIdParam || '');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Email state
  const [emailForm, setEmailForm] = useState({
    service_offering: '',
    tone: 'professional',
    email_type: 'cold'
  });
  const [generatedEmail, setGeneratedEmail] = useState(null);

  // Script state
  const [scriptForm, setScriptForm] = useState({
    service_offering: '',
    caller_name: '',
    company_name: ''
  });
  const [generatedScript, setGeneratedScript] = useState(null);

  useEffect(() => {
    fetchSavedLeads();
  }, []);

  useEffect(() => {
    if (leadIdParam) {
      setSelectedLeadId(leadIdParam);
    }
  }, [leadIdParam]);

  const fetchSavedLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/saved?limit=100`);
      setSavedLeads(response.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const generateEmail = async () => {
    if (!selectedLeadId) {
      toast.error('Please select a lead first');
      return;
    }
    if (!emailForm.service_offering) {
      toast.error('Please enter the service you want to offer');
      return;
    }

    setLoading(true);
    setGeneratedEmail(null);

    try {
      const response = await axios.post(`${API_URL}/ai/email`, {
        lead_id: selectedLeadId,
        ...emailForm
      });
      setGeneratedEmail(response.data.email);
      toast.success('Email generated!');
    } catch (error) {
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const generateScript = async () => {
    if (!selectedLeadId) {
      toast.error('Please select a lead first');
      return;
    }
    if (!scriptForm.service_offering || !scriptForm.caller_name || !scriptForm.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setGeneratedScript(null);

    try {
      const response = await axios.post(`${API_URL}/ai/script`, {
        lead_id: selectedLeadId,
        ...scriptForm
      });
      setGeneratedScript(response.data.script);
      toast.success('Script generated!');
    } catch (error) {
      toast.error('Failed to generate script');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedLead = savedLeads.find(l => l.id === selectedLeadId);

  return (
    <DashboardLayout>
      <Header 
        title="AI Tools" 
        subtitle="Generate personalized emails and cold call scripts"
      />
      
      <div className="p-8" data-testid="ai-tools-content">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="email" data-testid="email-tab">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Writer
                </TabsTrigger>
                <TabsTrigger value="script" data-testid="script-tab">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Script
                </TabsTrigger>
              </TabsList>

              <TabsContent value="email">
                <div className="card-dashboard">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    AI Email Generator
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <Label>Select Lead</Label>
                      <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                        <SelectTrigger className="mt-1" data-testid="lead-select-email">
                          <SelectValue placeholder="Choose a saved lead" />
                        </SelectTrigger>
                        <SelectContent>
                          {savedLeads.map((lead) => (
                            <SelectItem key={lead.id} value={lead.id}>
                              {lead.business_name} - {lead.city}, {lead.state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Service You're Offering *</Label>
                      <Input
                        value={emailForm.service_offering}
                        onChange={(e) => setEmailForm({ ...emailForm, service_offering: e.target.value })}
                        placeholder="e.g., SEO services, Facebook Ads, Web Development"
                        className="mt-1"
                        data-testid="service-input-email"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tone</Label>
                        <Select 
                          value={emailForm.tone}
                          onValueChange={(value) => setEmailForm({ ...emailForm, tone: value })}
                        >
                          <SelectTrigger className="mt-1" data-testid="tone-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="friendly">Friendly</SelectItem>
                            <SelectItem value="direct">Direct</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Email Type</Label>
                        <Select 
                          value={emailForm.email_type}
                          onValueChange={(value) => setEmailForm({ ...emailForm, email_type: value })}
                        >
                          <SelectTrigger className="mt-1" data-testid="email-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cold">Cold Email</SelectItem>
                            <SelectItem value="follow_up">Follow Up</SelectItem>
                            <SelectItem value="re_engagement">Re-engagement</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button 
                      onClick={generateEmail}
                      className="w-full btn-accent"
                      disabled={loading || !selectedLeadId}
                      data-testid="generate-email-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Generate Email</>
                      )}
                    </Button>
                  </div>

                  {/* Generated Email */}
                  {generatedEmail && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-slate-900">Generated Email</h4>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => copyToClipboard(`Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`)}
                            data-testid="copy-email-btn"
                          >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={generateEmail}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">Subject:</p>
                          <p className="text-slate-900 font-medium">{generatedEmail.subject}</p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500 mb-2">Body:</p>
                          <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{generatedEmail.body}</pre>
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="script">
                <div className="card-dashboard">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    AI Cold Call Script Generator
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <Label>Select Lead</Label>
                      <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                        <SelectTrigger className="mt-1" data-testid="lead-select-script">
                          <SelectValue placeholder="Choose a saved lead" />
                        </SelectTrigger>
                        <SelectContent>
                          {savedLeads.map((lead) => (
                            <SelectItem key={lead.id} value={lead.id}>
                              {lead.business_name} - {lead.city}, {lead.state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Service You're Offering *</Label>
                      <Input
                        value={scriptForm.service_offering}
                        onChange={(e) => setScriptForm({ ...scriptForm, service_offering: e.target.value })}
                        placeholder="e.g., SEO services, Facebook Ads"
                        className="mt-1"
                        data-testid="service-input-script"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Your Name *</Label>
                        <Input
                          value={scriptForm.caller_name}
                          onChange={(e) => setScriptForm({ ...scriptForm, caller_name: e.target.value })}
                          placeholder="John Smith"
                          className="mt-1"
                          data-testid="caller-name-input"
                        />
                      </div>
                      <div>
                        <Label>Your Company *</Label>
                        <Input
                          value={scriptForm.company_name}
                          onChange={(e) => setScriptForm({ ...scriptForm, company_name: e.target.value })}
                          placeholder="ABC Marketing"
                          className="mt-1"
                          data-testid="company-name-input"
                        />
                      </div>
                    </div>

                    <Button 
                      onClick={generateScript}
                      className="w-full btn-accent"
                      disabled={loading || !selectedLeadId}
                      data-testid="generate-script-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Generate Script</>
                      )}
                    </Button>
                  </div>

                  {/* Generated Script */}
                  {generatedScript && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-slate-900">Generated Script</h4>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={generateScript}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="space-y-4">
                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                          <p className="text-xs text-indigo-600 font-medium mb-2">Opening</p>
                          <p className="text-sm text-slate-700">{generatedScript.opening}</p>
                        </div>

                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                          <p className="text-xs text-emerald-600 font-medium mb-2">Hook</p>
                          <p className="text-sm text-slate-700">{generatedScript.hook}</p>
                        </div>

                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500 font-medium mb-2">Value Proposition</p>
                          <p className="text-sm text-slate-700">{generatedScript.value_proposition}</p>
                        </div>

                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500 font-medium mb-2">Discovery Questions</p>
                          <ul className="space-y-2">
                            {generatedScript.discovery_questions?.map((q, i) => (
                              <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                                <span className="text-indigo-600 font-medium">{i + 1}.</span>
                                {q}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                          <p className="text-xs text-orange-600 font-medium mb-2">Objection Handling</p>
                          <div className="space-y-3">
                            {generatedScript.objection_handling?.map((obj, i) => (
                              <div key={i} className="text-sm">
                                <p className="text-slate-500 italic">"{obj.objection}"</p>
                                <p className="text-slate-700 mt-1">→ {obj.response}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                          <p className="text-xs text-indigo-600 font-medium mb-2">Close</p>
                          <p className="text-sm text-slate-700">{generatedScript.close}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Selected Lead Info */}
          <div>
            <div className="card-dashboard sticky top-24">
              <h3 className="font-semibold text-slate-900 mb-4">Selected Lead</h3>
              
              {selectedLead ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-lg font-medium text-slate-900">{selectedLead.business_name}</p>
                    <p className="text-sm text-slate-500">{selectedLead.niche}</p>
                  </div>
                  <div className="text-sm text-slate-600">
                    <p>{selectedLead.city}, {selectedLead.state}</p>
                    {selectedLead.email && <p>{selectedLead.email}</p>}
                    {selectedLead.phone && <p>{selectedLead.phone}</p>}
                  </div>
                  {selectedLead.google_rating && (
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-yellow-500">★</span>
                      <span className="text-slate-700">{selectedLead.google_rating}</span>
                      <span className="text-slate-400">({selectedLead.review_count} reviews)</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">
                  Select a lead from your saved leads to generate personalized content.
                </p>
              )}

              {savedLeads.length === 0 && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    No saved leads yet. Search for leads and save them to use AI tools.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AITools;
